'use client';

/**
 * StopDetailSheet — one stop, up close: a big photo, what it is in the trip
 * (letter, role, nights, day), the drive on to the next stop, and a short
 * write-up (Wikipedia's summary for a town, free-licensed). Opens from a
 * stop's photo in the list or its pin on the map.
 *
 * Phones: a sheet that rises from the bottom over the planner. Wider
 * screens: a centred card. Either way a modal dialog: focus moves in, Tab
 * stays inside, Escape or the scrim closes it, focus goes back after.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import type { LadderLeg, TripStop } from './types';
import { ROLE_BADGE, roleAt, stopLetter } from './roles';
import { roleLabel } from '../../utils/tripRoles';
import { formatDrive, formatKm, hasCoords } from './geo';
import { PlaceImage } from './PlaceTile';
import { heroPhotoUrl, photoTitles } from './stopPhotos';
import { IconCompass, IconMoon, IconX } from './icons';
import { useModalFlag } from './ui';

export interface PlaceSummary {
  title: string;
  extract: string;
  image: string | null;
  url: string;
}

const summaries = new Map<string, Promise<PlaceSummary | null>>();

/** Wikipedia's lead summary for a place name (first title that has one). */
export function fetchPlaceSummary(name: string): Promise<PlaceSummary | null> {
  const titles = photoTitles(name);
  const key = titles[0];
  if (!key) return Promise.resolve(null);
  const cached = summaries.get(key);
  if (cached) return cached;
  const p = (async () => {
    for (const t of titles) {
      try {
        const res = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(t.replace(/ /g, '_'))}`);
        if (!res.ok) continue;
        const d = await res.json();
        // A disambiguation page ("Springdale may refer to…") says nothing.
        if (d?.type === 'disambiguation' || !d?.extract) continue;
        return {
          title: d.title as string,
          extract: d.extract as string,
          image: (d.thumbnail?.source as string | undefined) ?? null,
          url: (d.content_urls?.desktop?.page as string | undefined) ?? `https://en.wikipedia.org/wiki/${encodeURIComponent(t)}`,
        };
      } catch {
        /* next title */
      }
    }
    return null;
  })();
  summaries.set(key, p);
  // A network failure can be retried next time.
  p.then((v) => {
    if (!v) summaries.delete(key);
  });
  return p;
}

/** The day each stop is reached on (1-based): nights before it push it on. */
function stopDays(stops: Pick<TripStop, 'nights'>[]): number[] {
  const out: number[] = [];
  let day = 1;
  stops.forEach((s, i) => {
    if (i > 0) day += Math.max(0, stops[i - 1].nights ?? 0);
    out.push(day);
  });
  return out;
}

export default function StopDetailSheet({
  stops,
  stopId,
  legs,
  photo,
  onClose,
  onRemove,
  onOpenListing,
}: {
  stops: TripStop[];
  stopId: string;
  legs: LadderLeg[];
  /** The list's photo for this stop (a listing image or a Wikipedia thumb). */
  photo?: string | null;
  onClose: () => void;
  onRemove?: (stopId: string) => void;
  /** Campsites and parks we list: open the full listing. */
  onOpenListing?: (stopId: string) => void;
}) {
  const index = stops.findIndex((s) => s.id === stopId);
  const stop = index >= 0 ? stops[index] : null;
  const [summary, setSummary] = React.useState<PlaceSummary | null | undefined>(undefined);
  const [photoTry, setPhotoTry] = React.useState(0);
  const name = stop?.name ?? '';
  const isListing = typeof stop?.entityId === 'number';
  React.useEffect(() => {
    if (!name) return;
    let live = true;
    setSummary(undefined);
    setPhotoTry(0);
    // A listing has its own page; a town gets its encyclopaedia summary.
    if (isListing) {
      setSummary(null);
      return;
    }
    fetchPlaceSummary(name).then((s) => live && setSummary(s));
    return () => {
      live = false;
    };
  }, [name, isListing]);

  // The same modal flag as the planner's other dialogs (ui.tsx): the trip
  // sheet's grab handle and the chat bubble step back while it is up.
  useModalFlag(true);
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const closeRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const f = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')
      );
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      before?.focus?.();
    };
  }, [onClose]);

  if (!stop) return null;
  const role = roleAt(stops, index);
  const letter = stopLetter(index);
  const day = stopDays(stops)[index];
  const nights = stop.nights ?? 0;
  const next = index < stops.length - 1 ? stops[index + 1] : null;
  const leg = next ? legs.find((l) => l.fromId === stop.id) : undefined;
  const legFigures = [formatKm(leg?.distanceKm), formatDrive(leg?.driveMinutes)].filter(Boolean).join(' · ');
  // Best first: a 640px cut of the write-up's image, then the list's photo
  // made bigger, then the list's photo as it is. A size Wikimedia can't make
  // fails, and the next one is tried.
  const candidates = Array.from(
    new Set(
      [
        summary?.image ? summary.image.replace(/\/\d+px-/, '/640px-') : null,
        summary?.image ?? null,
        photo ? heroPhotoUrl(photo) : null,
        photo ?? null,
      ].filter((u): u is string => !!u)
    )
  );
  const big = candidates[Math.min(photoTry, candidates.length - 1)] ?? (summary === undefined && photo === undefined ? undefined : null);
  const dirUrl = hasCoords(stop) ? `https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}` : null;
  const titleId = `stop-detail-${stop.id}`;
  const roleWords = [
    roleLabel(role),
    nights > 0 ? `${nights} night${nights === 1 ? '' : 's'}` : null,
    `Day ${day}`,
  ]
    .filter(Boolean)
    .join(' · ');

  // Portalled to <body>: the planner sheet is transformed, which would pin
  // a fixed overlay to the sheet instead of the screen.
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center md:items-center" data-testid="stop-detail">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 min-h-0 w-full cursor-default border-0 bg-[rgba(10,18,32,0.42)] p-0 animate-tp-fade motion-reduce:animate-none"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-[20px] bg-white shadow-tp-pop animate-tp-sheet-up motion-reduce:animate-none md:max-w-[440px] md:rounded-[16px]"
      >
        <div className="relative flex-none">
          <PlaceImage
            src={big}
            kind={stop.kind === 'camp' ? 'camp' : 'town'}
            loading="eager"
            iconClassName="h-8 w-8"
            className="aspect-[16/9] w-full"
            title={big && /wikipedia|wikimedia/.test(big) ? `${stop.name} · photo via Wikimedia Commons` : undefined}
            onFail={() => setPhotoTry((n) => (n < candidates.length - 1 ? n + 1 : n))}
          />
          <span aria-hidden="true" className="absolute inset-x-0 top-0 h-16 bg-[linear-gradient(180deg,rgba(10,18,32,0.35),rgba(10,18,32,0))]" />
          <span aria-hidden="true" className="absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 rounded-full bg-white/80 md:hidden" />
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={`Close ${stop.name}`}
            className="absolute right-3 top-3 flex h-11 w-11 min-h-0 min-w-0 items-center justify-center rounded-full border-0 bg-white/95 p-0 text-tp-ink shadow-[0_1px_3px_rgba(22,35,58,0.25)] transition-colors duration-150 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
            data-testid="stop-detail-close"
          >
            <IconX className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-4 pt-4">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className={`mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full text-tp-sm font-bold ${ROLE_BADGE[role]}`}
            >
              {letter}
            </span>
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="m-0 text-[22px] font-semibold leading-7 tracking-[-0.015em] text-tp-ink [overflow-wrap:anywhere]">
                {stop.name}
              </h2>
              <p className="m-0 mt-0.5 flex items-center gap-1 text-tp-sm text-[#3a4a63]">
                <span className="sr-only">Stop {letter}. </span>
                {role === 'overnight' && <IconMoon className="h-3.5 w-3.5 flex-none" />}
                {roleWords}
              </p>
            </div>
          </div>

          {next && (
            <div className="mt-4 flex items-center gap-3 rounded-tp border border-solid border-tp-line px-3 py-2.5" data-testid="stop-detail-next">
              <span aria-hidden="true" className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-bold ${ROLE_BADGE[roleAt(stops, index + 1)]}`}>
                {stopLetter(index + 1)}
              </span>
              <p className="m-0 min-w-0 flex-1 text-tp-sm text-[#3a4a63]">
                Next: <span className="font-semibold text-tp-ink">{next.name}</span>
              </p>
              {legFigures && <span className="flex-none whitespace-nowrap text-tp-sm font-medium tabular-nums text-tp-ink">{legFigures}</span>}
            </div>
          )}

          {stop.notes && (
            <p className="m-0 mt-3 rounded-tp bg-tp-surface px-3 py-2 text-tp-sm text-tp-ink">{stop.notes}</p>
          )}

          <div className="mt-4" aria-live="polite">
            {summary === undefined ? (
              <div className="space-y-2" aria-label="Loading a write-up" data-testid="stop-detail-loading">
                <span className="block h-3.5 w-full animate-tp-pulse rounded bg-tp-surface motion-reduce:animate-none" />
                <span className="block h-3.5 w-11/12 animate-tp-pulse rounded bg-tp-surface motion-reduce:animate-none" />
                <span className="block h-3.5 w-3/4 animate-tp-pulse rounded bg-tp-surface motion-reduce:animate-none" />
              </div>
            ) : summary ? (
              <>
                <p className="m-0 line-clamp-[8] text-[15px] leading-[23px] text-tp-ink" data-testid="stop-detail-about">
                  {summary.extract}
                </p>
                <a
                  href={summary.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1.5 inline-flex min-h-[32px] items-center text-tp-sm font-semibold text-tp-brand underline decoration-tp-brand/30 underline-offset-2 hover:decoration-tp-brand"
                >
                  More on Wikipedia
                </a>
              </>
            ) : isListing ? (
              <p className="m-0 text-tp-sm text-[#3a4a63]">Sites, prices and photos are on its listing.</p>
            ) : (
              <p className="m-0 text-tp-sm text-[#3a4a63]">No write-up for this spot yet. The pin is where you put it.</p>
            )}
          </div>
        </div>

        <div className="flex flex-none items-center gap-2 border-0 border-t border-solid border-tp-line px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3">
          {isListing && onOpenListing ? (
            <button
              type="button"
              onClick={() => onOpenListing(stop.id)}
              className="h-11 min-h-0 flex-1 rounded-full border-0 bg-tp-brand px-4 text-tp-base font-semibold text-white transition-colors duration-150 hover:bg-tp-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 focus-visible:ring-offset-2"
            >
              Open listing
            </button>
          ) : null}
          {dirUrl && (
            <a
              href={dirUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex h-11 items-center justify-center gap-1.5 rounded-full border border-solid border-tp-line-strong bg-white px-4 text-tp-base font-semibold text-tp-ink no-underline transition-colors duration-150 hover:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 ${
                isListing && onOpenListing ? 'flex-none' : 'flex-1'
              }`}
            >
              <IconCompass className="h-4 w-4" />
              Directions
            </a>
          )}
          {onRemove && !stop.locked && (
            <button
              type="button"
              onClick={() => {
                onRemove(stop.id);
                onClose();
              }}
              className="h-11 min-h-0 flex-none rounded-full border-0 bg-transparent px-3 text-tp-base font-semibold text-tp-danger transition-colors duration-150 hover:bg-tp-danger/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-danger/40"
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
